import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';  // NEW: Import Auth
import useSocket from '../../../hooks/useSocket';

/**
 * useGameRoom - Hook para gestión de salas de juego
 * Encapsula la lógica de creación, unión y listado de salas.
 * 
 * @param {string} gameId - Identificador del juego (ej: "guess-who")
 * @returns {object} { rooms, currentRoom, createRoom, joinRoom, leaveRoom, loading, error, connected }
 */
const useGameRoom = (gameId) => {
    const { socket, connected, error: socketError } = useSocket();
    const [rooms, setRooms] = useState([]);
    const [currentRoom, setCurrentRoom] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Limpiar error cuando hay conexión exitosa
    useEffect(() => {
        if (connected) setError(null);
    }, [connected]);

    // Gestión de eventos de sala
    useEffect(() => {
        if (!connected || !socket) return;

        // Solicitar lista inicial
        socket.emit('list_rooms', { game_id: gameId });

        // Listeners
        const handleRoomList = (data) => {
            console.log('[useGameRoom] 📋 Room List Received:', data);
            setLoading(false);
            if (data.success) {
                const rooms = data.data || [];
                console.log(`[useGameRoom] ✅ ${rooms.length} rooms available:`, rooms);
                setRooms(rooms);
            } else {
                console.warn('[useGameRoom] ❌ Error fetching rooms:', data.message);
            }
        };

        const handleRoomJoined = (data) => {
            setLoading(false);
            if (data.success) {
                // NOTE: We do NOT sync userId here anymore to avoid overwriting our ID 
                // when OTHER players join. We rely on the 'join_room' Ack for our own identity.

                // Only update currentRoom if we don't have one? 
                // Actually, the Ack sets it for us. This listener notifies us of OTHERS.
                // But wait, 'room_joined' is sent to the whole room.
                // If we are already in the room, we get this for others.
                // If we JUST joined, we get this too. 
                // But the Ack handles the "My Join" case.

                // Proceed with caution: simple logging or UI updates for "X joined" are fine here.
                // Logic for "Entering the room view" is handled by the Ack.
            } else {
                setError(data.message || 'Failed to join room');
            }
        };

        const handleRoomCreated = (data) => {
            setLoading(false);
            if (data.success) {
                setCurrentRoom(data.data.room_id);
            } else {
                setError(data.message || 'Failed to create room');
            }
        };

        const handleRoomGenericError = (data) => {
            setLoading(false);
            setError(data.message || 'Unknown error');
        };

        socket.on('room_list', handleRoomList);
        socket.on('room_joined', handleRoomJoined);
        socket.on('room_created', handleRoomCreated);
        socket.on('error', handleRoomGenericError);

        // Escuchar actualizaciones de la lista (Realtime Lobby - Fix 11)
        const handleRoomListUpdate = (data) => {
            // Si el evento es para este juego (o global), actualizamos
            if (!data.game_id || data.game_id === gameId) {
                console.log(`[useGameRoom] 🔄 Received room_list_update for ${data.game_id || 'all games'}`);
                socket.emit('list_rooms', { game_id: gameId });
            }
        };

        socket.on('room_list_update', handleRoomListUpdate);

        return () => {
            socket.off('room_list', handleRoomList);
            socket.off('room_joined', handleRoomJoined);
            socket.off('room_created', handleRoomCreated);
            socket.off('error', handleRoomGenericError);
            socket.off('room_list_update', handleRoomListUpdate);
        };
    }, [connected, socket, gameId]);

    const { user } = useAuth();  // NEW: Get user from Auth context

    const createRoom = useCallback(() => {
        if (!connected) return;
        setLoading(true);
        setError(null);
        // ID único temporal, el backend podría asignarlo o validarlo
        const roomId = `${gameId}-${Date.now().toString().slice(-4)}`;
        socket.emit('create_room', {
            game_id: gameId,
            room_id: roomId,
            username: user?.name || 'Unknown'  // NEW: Send username from Auth
        });
    }, [connected, socket, gameId, user]);  // NEW: Add user to dependencies

    const joinRoom = useCallback((roomId) => {
        if (!connected) return;
        setLoading(true);
        setError(null);

        // Use Acknowledgement pattern for robust joining and identity retrieval
        socket.emit('join_room', {
            room_id: roomId,
            username: user?.name || 'Unknown'  // NEW: Send username from Auth
        }, (ack) => {
            setLoading(false);

            if (ack && ack.success) {
                console.log(`[useGameRoom] ✅ Joined Room via Ack`, ack);
                // Securely sync identity from the Ack (only for ME)
                if (ack.data.user_id) {
                    localStorage.setItem('userId', ack.data.user_id);
                    console.log(`[useGameRoom] 🆔 Synced userId to localStorage: ${ack.data.user_id}`);
                }
                if (ack.data.username) {
                    localStorage.setItem('username', ack.data.username);
                }
                setCurrentRoom(ack.data.room_id);
            } else {
                console.error(`[useGameRoom] ❌ Join Room Failed:`, ack);
                setError(ack?.message || 'Failed to join room');
            }
        });
    }, [connected, socket, user]);  // NEW: Add user to dependencies

    const leaveRoom = useCallback(() => {
        if (!currentRoom || !connected) return;
        socket.emit('leave_room', { room_id: currentRoom });
        setCurrentRoom(null);
    }, [currentRoom, connected, socket]);

    return {
        rooms,
        currentRoom,
        createRoom,
        joinRoom,
        leaveRoom,
        loading,
        error: error || socketError,
        connected
    };
};

export default useGameRoom;
